import { Loader2Icon } from 'lucide-react'
import { memo, type ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface BillingPlanCardButtonProps {
  isCurrentPlan?: boolean
  isCancelling?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  isFreePlan?: boolean
  isPopular?: boolean
  onClick?: () => void
  className?: string
}

type ButtonVariant = NonNullable<React.ComponentProps<typeof Button>['variant']>

interface ButtonState {
  isCurrentPlan: boolean
  isCancelling: boolean
  isFreePlan: boolean
  isPopular: boolean
}

const getButtonVariant = ({
  isCurrentPlan,
  isCancelling,
  isFreePlan,
  isPopular,
}: ButtonState): ButtonVariant => {
  const isResubscribe = isCancelling && isCurrentPlan
  if (isResubscribe) return 'default'
  if (isCurrentPlan) return 'outline'
  if (isFreePlan) return 'destructive'
  if (isPopular) return 'default'
  return 'secondary'
}

const getButtonLabel = (state: ButtonState): string => {
  const { isCurrentPlan, isCancelling, isFreePlan } = state
  if (isCancelling && isCurrentPlan) return 'Resubscribe'
  if (isCurrentPlan) return 'Current Plan'
  if (isFreePlan) return 'Cancel Subscription'
  return 'Select Plan'
}

const getIsButtonDisabled = (
  state: ButtonState,
  isLoading: boolean,
  isDisabled: boolean
): boolean => {
  const { isCurrentPlan, isCancelling } = state
  const showResubscribe = isCancelling && isCurrentPlan
  return (isCurrentPlan && !showResubscribe) || isLoading || isDisabled
}

const LoadingContent = () => (
  <>
    <Loader2Icon className="mr-2 size-4 animate-spin" />
    Processing...
  </>
)

const BillingPlanCardButtonBase: React.FC<BillingPlanCardButtonProps> = ({
  isCurrentPlan = false,
  isCancelling = false,
  isLoading = false,
  isDisabled = false,
  isFreePlan = false,
  isPopular = false,
  onClick,
  className,
}): ReactNode => {
  const state: ButtonState = { isCurrentPlan, isCancelling, isFreePlan, isPopular }

  return (
    <Button
      variant={getButtonVariant(state)}
      className={cn('billing-plan-select-button w-full', className)}
      disabled={getIsButtonDisabled(state, isLoading, isDisabled)}
      onClick={onClick}
    >
      {isLoading ? <LoadingContent /> : getButtonLabel(state)}
    </Button>
  )
}

export const BillingPlanCardButton = memo(BillingPlanCardButtonBase)

BillingPlanCardButton.displayName = 'BillingPlanCardButton'
